import React, {
  useRef,
  useState,
  Dispatch,
  RefObject,
  forwardRef,
  SetStateAction,
  MutableRefObject,
  useImperativeHandle,
  ForwardRefRenderFunction,
} from 'react';
import Grid from '@mui/material/Grid';
import Accordion from '@mui/material/Accordion';
import { OptionGroup } from 'react-querybuilder';
import { RemoveRedEye } from '@mui/icons-material';
import FormControl from '@mui/material/FormControl';
import { IconButton, Tooltip } from '@mui/material';
import FormHelperText from '@mui/material/FormHelperText';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CircularProgress from '@mui/material/CircularProgress';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';

import {
  DataProfile,
  AlarmDefinition,
  QueryPreviewType,
  DataSourceAlarmMetadata,
  QueryBuilderValidation,
  SelectedQueryBuilderField,
} from 'types/types';
import {
  transformAttributesArray,
  getUserEnteredValueOperators,
} from '../../QueryBuilder/helper';
import NIService, { INIService } from 'services/NIService';
import { TargetCriteriaValidationError } from '../CreateForm';
import { Ref as AlarmManagementFormRulesRef } from '../FormRules/FormRules';
import { Ref as AlarmManagementFormDetailsRef } from '../FormDetails/FormDetails';

import AlarmManagementDataProfiles, {
  ProfileOption,
  Ref as AlarmManagementDataProfilesRef,
} from './DataProfiles';
import QueryBuilder, {
  Ref as QueryBuilderRef,
  QueryBuilderType,
} from '../../QueryBuilder/QueryBuilder';
import AlarmManagementQueryPreview, {
  Ref as AlarmManagementQueryPreviewRef,
} from '../../QueryPreview/QueryPreview';
import SQLQueryContainer, {
  Ref as SQLQueryContainerRef,
} from '../SQLQueryContainer/SQLQueryContainer';
import Modal, { Ref as ModalRef } from '../../Modal/Modal';
import { Ref as AlarmMangementOutputRef } from '../Output/Output';

interface Props {
  queryOperators: OptionGroup[];
  loadingQueryBuilderData: boolean;
  alarmDefinitionData: AlarmDefinition | null;
  hasQueryBuilderEmptyValues: (
    query: any,
    dataSourceMetadataForAlarm?: DataSourceAlarmMetadata
  ) => QueryBuilderValidation;
  selectedRulesRef: React.RefObject<SelectedQueryBuilderField>;
  dataSourceMetadataForAlarm: DataSourceAlarmMetadata | undefined;
  alarmOutputRef: React.MutableRefObject<AlarmMangementOutputRef | null>;
  alarmManagementFormRulesRef: React.MutableRefObject<AlarmManagementFormRulesRef | null>;
  alarmManagementFormDetailsRef: React.MutableRefObject<AlarmManagementFormDetailsRef | null>;
}

export interface Ref {
  refs: TargetCriteriaRef;
  handleChangeDataProfiles: (dataProfiles: DataProfile[]) => void;
  queryPreviewIconRef: React.MutableRefObject<QueryPreviewIconRef | null>;
}

interface TargetCriteriaRef {
  targetCriteriaValidation: Dispatch<
    SetStateAction<TargetCriteriaValidationError>
  >;
  queryBuilder: MutableRefObject<QueryBuilderRef | null>;
  alarmManagementDataProfilesRef: RefObject<AlarmManagementDataProfilesRef>;
}

interface QueryPreviewIconRef {
  disableQueryPreview: (aggregationLevel: string) => void;
}

interface QueryPreviewIconProps {
  handleShowPreviewClick: () => void;
  checkDisabledQueryPreview: (aggregationLevel: string) => boolean;
  alarmManagementFormRulesRef: React.MutableRefObject<AlarmManagementFormRulesRef | null>;
}

const ForwardRefQueryPreviewIcon: ForwardRefRenderFunction<
  QueryPreviewIconRef,
  QueryPreviewIconProps
> = (
  {
    handleShowPreviewClick,
    checkDisabledQueryPreview,
    alarmManagementFormRulesRef,
  },
  ref
) => {
  const [disabledIcon, setDisabledIcon] = useState<boolean>(
    checkDisabledQueryPreview(
      alarmManagementFormRulesRef.current?.refs.aggregationLevel?.current?.value
    )
  );

  const disableQueryPreview = (aggregationLevel: string) => {
    const disableIcon = checkDisabledQueryPreview(aggregationLevel);
    setDisabledIcon(disableIcon);
  };

  useImperativeHandle(ref, () => ({ disableQueryPreview }));

  return (
    <Tooltip title="Show Preview">
      <IconButton onClick={handleShowPreviewClick} disabled={disabledIcon}>
        <RemoveRedEye />
      </IconButton>
    </Tooltip>
  );
};

const QueryPreviewIcon = forwardRef<QueryPreviewIconRef, QueryPreviewIconProps>(
  ForwardRefQueryPreviewIcon
);

const TargetCriteria: ForwardRefRenderFunction<Ref, Props> = (
  {
    alarmOutputRef,
    queryOperators,
    selectedRulesRef,
    alarmDefinitionData,
    loadingQueryBuilderData,
    hasQueryBuilderEmptyValues,
    dataSourceMetadataForAlarm,
    alarmManagementFormRulesRef,
    alarmManagementFormDetailsRef,
  },
  ref
) => {
  const initialErrors: TargetCriteriaValidationError = {
    queryBuilder: '',
    dataProfile: '',
  };

  const modalRef = useRef<ModalRef | null>(null);
  const alarmManagementQueryPreviewRef =
    useRef<AlarmManagementQueryPreviewRef | null>(null);
  const queryBuilderRef = useRef<QueryBuilderRef | null>(null);
  const queryPreviewIconRef = useRef<QueryPreviewIconRef | null>(null);
  const sqlQueryContainerRef = useRef<SQLQueryContainerRef | null>(null);
  const alarmManagementDataProfilesRef =
    useRef<AlarmManagementDataProfilesRef>(null);

  const [validationErrors, setValidationErrors] =
    useState<TargetCriteriaValidationError>(initialErrors);
  const [dataProfiles, setDataProfiles] = useState<DataProfile[]>([]);
  const [showQueryBuilder, setShowQueryBuilder] = useState<boolean>(false);

  const handleShowQueryBuilder = (show: boolean) => setShowQueryBuilder(show);

  const refs: TargetCriteriaRef = {
    queryBuilder: queryBuilderRef,
    targetCriteriaValidation: setValidationErrors,
    alarmManagementDataProfilesRef: alarmManagementDataProfilesRef,
  };

  const handleOnChange = (field?: string) => {
    switch (field?.toUpperCase()) {
      case 'QUERYBUILDER':
        if (
          !hasQueryBuilderEmptyValues(
            queryBuilderRef.current?.query,
            dataSourceMetadataForAlarm
          )
        )
          setValidationErrors((prev) => {
            return { ...prev, queryBuilder: '' };
          });
        break;
      case 'DATAPROFILE':
        if (validationErrors.dataProfile !== '') {
          setValidationErrors((prev) => {
            return { ...prev, dataProfile: '' };
          });
        }
        break;
      default:
        break;
    }
  };

  const handleChangeDataProfiles = (dataProfiles: DataProfile[]) => {
    setDataProfiles(dataProfiles);
    setShowQueryBuilder(true);
    alarmManagementDataProfilesRef.current?.handleProfileClick(
      ProfileOption.CUSTOM
    )();
  };

  const fetchQueryPreviewByDataProfile = async (dataProfile: DataProfile) => {
    const niService: INIService = new NIService();
    try {
      alarmManagementQueryPreviewRef.current?.handleShowLoader(true);
      niService
        .GetQueryPreviewByDataProfile(dataProfile)
        .then((data: { data: QueryPreviewType }) => {
          alarmManagementQueryPreviewRef.current?.handleQueryPreview(data.data);
        })
        .finally(() => {
          alarmManagementQueryPreviewRef.current?.handleShowLoader(false);
        });
    } catch (error) {
      console.error('Failed to fetch query preview', error);
    }
  };

  const fetchQueryPreviewByQueryRule = async (
    queryRule: any,
    aggregationLevelId: string,
    sourceElementId: string
  ) => {
    const niService: INIService = new NIService();
    try {
      alarmManagementQueryPreviewRef.current?.handleShowLoader(true);
      niService
        .GetQueryPreviewByQueryRule(
          queryRule,
          sourceElementId,
          aggregationLevelId
        )
        .then((data: { data: QueryPreviewType }) => {
          alarmManagementQueryPreviewRef.current?.handleQueryPreview(data.data);
        })
        .finally(() => {
          alarmManagementQueryPreviewRef.current?.handleShowLoader(false);
        });
    } catch (error) {
      console.error('Failed to fetch query preview', error);
    }
  };

  const handleShowModal = (show: boolean) =>
    modalRef.current?.handleShowModal(show);

  const handleQueryPreview = async () => {
    if (
      alarmManagementDataProfilesRef.current?.selectedProfileOption ===
      ProfileOption.PROFILE
    ) {
      const dataProfileId =
        alarmManagementDataProfilesRef.current.dataProfileRef.current?.value;
      const dataProfile = dataProfiles.find(
        (el) => el.profileSid === +(dataProfileId ?? '-1')
      );
      if (dataProfile) {
        await fetchQueryPreviewByDataProfile(dataProfile);
      }
    } else if (
      !alarmManagementDataProfilesRef.current?.selectedProfileOption ||
      alarmManagementDataProfilesRef.current?.selectedProfileOption ===
        ProfileOption.CUSTOM
    ) {
      const aggregationLevelId =
        alarmManagementFormRulesRef.current?.refs.aggregationLevel?.current
          ?.value;
      const queryRule = queryBuilderRef.current?.query;
      const sourceElementId =
        alarmManagementFormDetailsRef.current?.refs.niDataSource.current?.value;
      if (queryRule && sourceElementId && aggregationLevelId) {
        await fetchQueryPreviewByQueryRule(
          queryRule,
          aggregationLevelId,
          sourceElementId
        );
      }
    }
  };

  const checkDisabledQueryPreview = (aggregationLevel: string) => {
    if (
      !aggregationLevel ||
      aggregationLevel == '' ||
      aggregationLevel == `'Network'`
    ) {
      return true;
    }

    return false;
  };

  const handleShowPreviewClick = () => {
    handleShowModal(true);
    setTimeout(() => {
      handleQueryPreview();
    }, 100);
  };

  useImperativeHandle(ref, () => ({
    refs,
    queryPreviewIconRef,
    handleChangeDataProfiles,
  }));

  return (
    <div className="alarm-form-card-container">
      {loadingQueryBuilderData && (
        <div className="loading-overlay">
          <CircularProgress color="warning" className="circular-progess-icon" />
        </div>
      )}
      <div className="alarm-form-card-title-container">
        <span className="alarm-form-card-title">Target Criteria</span>
      </div>

      {dataProfiles.length > 0 && (
        <div>
          <AlarmManagementDataProfiles
            dataProfiles={dataProfiles}
            handleOnChange={handleOnChange}
            validationErrors={validationErrors}
            ref={alarmManagementDataProfilesRef}
            alarmDefinitionData={alarmDefinitionData}
            handleShowQueryBuilder={handleShowQueryBuilder}
            handleShowPreviewClick={handleShowPreviewClick}
          />
        </div>
      )}

      {showQueryBuilder && (
        <div className="alarm-form-elements-container">
          <Grid container spacing={2} sx={{ alignItems: 'center' }}>
            <Grid item xs={5}>
              <Accordion
                disabled={!dataSourceMetadataForAlarm}
                className="accordion"
              >
                <AccordionSummary
                  className={'accordion-summary'}
                  expandIcon={<ExpandMoreIcon />}
                >
                  SQL Output
                </AccordionSummary>
                <AccordionDetails>
                  <div>
                    <SQLQueryContainer
                      ref={sqlQueryContainerRef}
                      queryOperators={queryOperators}
                      dataSourceMetadataForAlarm={dataSourceMetadataForAlarm}
                    />
                  </div>
                </AccordionDetails>
              </Accordion>
            </Grid>
            <Grid item xs={1}>
              <QueryPreviewIcon
                ref={queryPreviewIconRef}
                handleShowPreviewClick={handleShowPreviewClick}
                checkDisabledQueryPreview={checkDisabledQueryPreview}
                alarmManagementFormRulesRef={alarmManagementFormRulesRef}
              />
            </Grid>
          </Grid>
          <div className="query-builder-container">
            <FormControl fullWidth error={!!validationErrors.queryBuilder}>
              <QueryBuilder
                type={QueryBuilderType.TARGETCRITERIA}
                selectedRulesRef={selectedRulesRef}
                alarmOutputRef={alarmOutputRef}
                queryValue={
                  alarmDefinitionData?.target_element_criteria_serialized
                }
                fields={
                  dataSourceMetadataForAlarm
                    ? transformAttributesArray(
                        dataSourceMetadataForAlarm.dimAttributes,
                        queryOperators
                      )
                    : []
                }
                dataSourceMetadataForAlarm={dataSourceMetadataForAlarm}
                ref={queryBuilderRef}
                onChange={() => handleOnChange('QueryBuilder')}
                disabled={!dataSourceMetadataForAlarm}
                defaultOperators={getUserEnteredValueOperators(queryOperators)}
                handleUpdateSQLQuery={
                  sqlQueryContainerRef.current?.handleUpdateSQLQuery
                }
              />
              <FormHelperText>{validationErrors.queryBuilder}</FormHelperText>
            </FormControl>
          </div>
        </div>
      )}
      <Modal titleElement="Preview" enableActionButton={false} ref={modalRef}>
        <AlarmManagementQueryPreview ref={alarmManagementQueryPreviewRef} />
      </Modal>
    </div>
  );
};

export default forwardRef<Ref, Props>(TargetCriteria);
